import { faArrowRight, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card } from "@mui/material";
import { delay, fullAddress, styles } from "@repo/helpers";
import { Button, ButtonColor, ButtonTypes, Loading, TextBox } from "@repo/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { FC, useState } from "react";

import { AddressApi } from "./api";
import style from "./style.module.scss";
import { AutocompleteItem } from "./types";

interface Props {
	id: string;
	value: AutocompleteItem;
	defaultZip?: string;
	validationError?: string;
	disabled: boolean;
	readOnly: boolean;
	changeHandler: (value: AutocompleteItem, id: string) => void;
}

async function searchAddress(inputValue: string, selectedValue: string, zipValue: string) {
	if (inputValue.length < 2) return [];

	const values = await AddressApi.searchAddress(inputValue, selectedValue, zipValue);
	return values.suggestions.map((i) => ({
		value: i,
		label: fullAddress(i.addressLine1, i.addressLine2, i.city, i.state, i.zip),
	}));
}

export const Autocomplete: FC<Props> = ({
	id,
	value,
	defaultZip,
	disabled,
	readOnly,
	validationError,
	changeHandler,
}) => {
	const [selected, setSelected] = useState<string>("");
	const [open, setOpen] = useState(false);
	const query = useQuery({
		placeholderData: keepPreviousData,
		queryKey: ["AddressAutocomplete", value.formatted, selected, defaultZip],
		queryFn: async ({ signal }) => {
			if (value.addressLine1) return []; //To avoid lookup up on a selected address
			await delay(500);
			if (!signal?.aborted) {
				return searchAddress(value.formatted, selected, defaultZip);
			}
		},
	});

	const handleAddressChange = (newValue: string) => {
		updateValue(newValue);
		setSelected("");
		setOpen(true);
	};

	const handleMoreAddresses = (value: AutocompleteItem) => {
		setOpen(false);
		setSelected(
			fullAddress(
				value.addressLine1,
				`${value.addressLine2} (${value.entries})`,
				value.city,
				value.state,
				value.zip,
			),
		);
		setOpen(true);
	};

	const handleSelect = async (value: AutocompleteItem) => {
		const address = fullAddress(value.addressLine1, value.addressLine2, value.city, value.state, value.zip);

		changeHandler({ formatted: address, ...value }, id);
		setOpen(false);
	};

	const updateValue = (formatted: string) => {
		changeHandler(
			{
				formatted,
				addressLine1: "",
				addressLine2: "",
				city: "",
				state: "",
				zip: "",
				entries: undefined,
			},
			id,
		);
	};

	const handleClear = () => {
		setSelected("");
		setOpen(false);
		updateValue("");
	};

	return (
		<div>
			<TextBox
				label="Address"
				value={value.formatted}
				className="w-100"
				validationError={validationError}
				readOnly={readOnly}
				disabled={readOnly || disabled}
				autocomplete="noop"
				changeHandler={handleAddressChange}
				clear={handleClear}
				includeError
			/>
			{open ? (
				<Card>
					<Loading loading={query.isFetching} message="Loading addresses..." className="py-4">
						<ul className="list-group">
							{query.data?.map((i) => (
								<li key={i.label} className={styles("list-group-item", style.listItem)}>
									<div className="flex justify-between">
										<button onClick={() => handleSelect(i.value)} className={style.autocompleteBtn}>
											{i.label}
										</button>
										{i.value.entries > 1 ? (
											<Button
												onClick={() => handleMoreAddresses(i.value)}
												color={ButtonColor.DarkBlue}
												types={[ButtonTypes.Text]}
											>
												<FontAwesomeIcon icon={faPlus} className="px-1" />
												{i.value.entries} Addresses
												<FontAwesomeIcon icon={faArrowRight} className="px-1" />
											</Button>
										) : null}
									</div>
								</li>
							))}
						</ul>
					</Loading>
				</Card>
			) : null}
		</div>
	);
};

// async function searchAddress(inputValue: string, selectedValue: string, zipValue: string) {
//     if (inputValue.length < 2) return [];
//     const credentials = new core.SharedCredentials(import.meta.env.VITE_SMARTY_API_KEY as string);
//     const clientBuilder = new core.ClientBuilder(credentials); //.withLicenses(["us-autocomplete-pro-cloud"]);
//     const client = clientBuilder.buildUsAutocompleteProClient();
//     const lookup = new usAutocompletePro.Lookup(inputValue);
//
//     lookup.selected = selectedValue;
//     lookup.includeOnlyZIPCodes = [zipValue];
//
//     try {
//         const results = await client.send(lookup);
//
//         return results.result.map((i) => ({
//             value: {
//                 addressLine1: i.streetLine,
//                 addressLine2: i.secondary,
//                 city: i.city,
//                 state: i.state,
//                 zip: i.zipcode,
//                 entries: i.entries,
//             },
//             label: format.fullAddress(i.streetLine, i.secondary, i.city, i.state, i.zipcode),
//         }));
//     } catch (ex) {
//         log(ex, "error");
//         return [];
//     }
// }
